/**
 * Contratos de los endpoints del maestro de artículos:
 *   - `GET /api/productos/buscar?sku=...`  → ficha completa de un artículo.
 *   - `GET /api/productos/listar?sku=&nombre=` → filtro del maestro.
 *
 * El backend pega a ACSIS y normaliza los shapes.
 */

import { z } from 'zod';

export const ProductoClasificadoresSchema = z.object({
  c1: z.string().nullable(),
  c2: z.string().nullable(),
  c3: z.string().nullable(),
  c4: z.string().nullable(),
  c5: z.string().nullable(),
  c6: z.string().nullable(),
  c7: z.string().nullable(),
  c8: z.string().nullable(),
});

export const ProductoFlagsSchema = z.object({
  controlaStock: z.boolean(),
  conPartidas: z.boolean(),
  conNrosDeSerie: z.boolean(),
  conTalles: z.boolean(),
  seVende: z.boolean(),
  seCompra: z.boolean(),
  seProduce: z.boolean(),
  bloqueoParaMovimientoStock: z.boolean(),
  generaMovimientoDeStock: z.boolean(),
  conPPP: z.boolean(),
});

export const ProductoVentasSchema = z.object({
  rubroVenta: z.string().nullable(),
  bloqueadoParaVentas: z.boolean(),
  cuentaVentas: z.string().nullable(),
  prestacion: z.boolean(),
  cantidadMinimaPedido: z.number().nullable(),
  esBandeja: z.boolean(),
  esPallet: z.boolean(),
  esConjunto: z.boolean(),
});

export const ArticuloAcsisSchema = z.object({
  sku: z.string().nullable(),
  articuloInterno: z.string().nullable(),
  nombre: z.string().nullable(),
  descripcion: z.string().nullable(),
  articuloImpresion: z.string().nullable(),
  tipoArticulo: z.string().nullable(),
  codigoDeBarras: z.string().nullable(),
  unidadDeMedida: z.string().nullable(),
  cuentaContable: z.string().nullable(),
  fechaAlta: z.string().nullable(),
  clasificadores: ProductoClasificadoresSchema,
  flags: ProductoFlagsSchema,
  pesoEmbalado: z.number().nullable(),
  cantidadDeBultos: z.number().nullable(),
  stockMinimo: z.number().nullable(),
  ventas: ProductoVentasSchema.nullable(),
});

export const ProductoBuscarResponse = z.object({
  articulo: ArticuloAcsisSchema,
  raw: z.unknown(),
});

export type ArticuloAcsis = z.infer<typeof ArticuloAcsisSchema>;
export type ProductoBuscar = z.infer<typeof ProductoBuscarResponse>;

/**
 * Fila del listado (`GET /api/productos/listar`). Es un shape más pobre que la
 * ficha: el endpoint de listado de ACSIS no devuelve el bloque `ventas` ni los
 * flags completos, solo lo suficiente para elegir un artículo de la tabla.
 */
export const ArticuloListItemSchema = z.object({
  sku: z.string().nullable(),
  nombre: z.string().nullable(),
  descripcion: z.string().nullable(),
  tipoArticulo: z.string().nullable(),
  codigoDeBarras: z.string().nullable(),
  unidadDeMedida: z.string().nullable(),
  trazable: z.boolean(),
  seVende: z.boolean(),
  fechaAlta: z.string().nullable(),
});

export const ProductoListarResponse = z.object({
  items: z.array(ArticuloListItemSchema),
  /** Coincidencias totales (puede superar `items.length` si hay tope). */
  total: z.number(),
  truncado: z.boolean(),
  /** Tamaño del maestro completo, para contextualizar el filtro. */
  totalMaestro: z.number(),
  /** Cuándo se trajo el maestro desde ACSIS (el back lo cachea). */
  sincronizadoEn: z.string().nullable(),
});

export type ArticuloListItem = z.infer<typeof ArticuloListItemSchema>;
export type ProductoListar = z.infer<typeof ProductoListarResponse>;
